import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft,
  Star,
  MapPin,
  Users,
  Calendar,
  Award,
  BookOpen,
  Share2,
  CheckCircle,
  Building2,
  Globe,
  Phone,
  Mail,
  ExternalLink,
  ThumbsUp,
  MessageCircle,
  User,
  Filter,
  GraduationCap,
  TrendingUp,
  Shield,
  Clock,
  Eye,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Target,
  Zap,
  Facebook,
  Linkedin
} from 'lucide-react';
import { trainingCentersService, TrainingCenter, TrainingCenterReview, CreateTrainingCenterReviewData } from '../../services/training-centers.service';
import { useAuth } from '../../context/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import traineeService from '../../services/trainee.service';

const TrainingCenterDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [center, setCenter] = useState<(TrainingCenter & { 
    viewCount?: number;
    address?: string;
    ratingStats?: {
      averageRating: number;
      totalReviews: number;
      ratingDistribution: { 1: number; 2: number; 3: number; 4: number; 5: number };
    };
  }) | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<TrainingCenterReview[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<'all' | '5' | '4' | '3' | '2' | '1'>('all');
  const [selectedRating, setSelectedRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [centerTrainings, setCenterTrainings] = useState<any[]>([]);
  const [similarCenters, setSimilarCenters] = useState<TrainingCenter[]>([]);
  const [isSubscribedToCenter, setIsSubscribedToCenter] = useState<boolean>(false);
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    description: false,
    services: false,
    certifications: false,
    contact: false
  });

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    if (id) {
      loadCenter();
      loadReviews();
      loadCenterTrainings();
      loadSimilarCenters();
      checkSubscriptionStatus();
    }
  }, [id, isAuthenticated, user]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadCenter = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const centerData = await trainingCentersService.getTrainingCenter(id!);
      setCenter(centerData);
    } catch (error) {
      setError(t('public.trainingCenterDetail.errors.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const reviewsData = await trainingCentersService.getCenterReviews(id!);
      setReviews(reviewsData);
    } catch (error) {
    }
  };

  const loadCenterTrainings = async () => {
    try {
      const trainingsData = await trainingCentersService.getCenterTrainings(id!, {});
      setCenterTrainings(trainingsData || []);
    } catch (error) {
    }
  };

  const loadSimilarCenters = async () => {
    try {
      const similarCentersData = await trainingCentersService.getSimilarCenters(id!, 3);
      setSimilarCenters(similarCentersData);
    } catch (error) {
    }
  };

  const checkSubscriptionStatus = async () => {
    if (!isAuthenticated || !user || !id) {
      setIsSubscribedToCenter(false);
      return;
    }

    // Check if user is a trainee (role_id 3 or role.name === 'trainee')
    const isTrainee = user.role_id === 3 || user.role?.name === 'trainee';
    if (!isTrainee) {
      setIsSubscribedToCenter(false);
      return;
    }

    try {
      // Get all trainings the trainee is enrolled in
      const myTrainings = await traineeService.getMyTrainings();
      
      // Check if any of the trainee's trainings belong to this center
      const centerIdNumber = parseInt(id);
      const isSubscribed = myTrainings.some(training => 
        training.center && training.center.id === centerIdNumber
      );
      
      setIsSubscribedToCenter(isSubscribed);
    } catch (error) {
      setIsSubscribedToCenter(false);
    }
  };


  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: center?.name,
        text: t('public.trainingCenterDetail.share.text', { name: center?.name }),
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show toast notification
    }
  };

  const handleReviewSubmit = async () => {
    if (!isAuthenticated || !center || selectedRating === 0) return;

    try {
      setIsSubmittingReview(true);
      const reviewData: CreateTrainingCenterReviewData = {
        rating: selectedRating,
        comment: reviewComment,
      };

      await trainingCentersService.createCenterReview(center.id, reviewData);
      setShowReviewForm(false);
      setSelectedRating(0);
      setReviewComment('');
      loadReviews();
    } catch (error) {
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (reviewFilter === 'all') return true;
    return review.rating.toString() === reviewFilter;
  });

  const formatRating = (rating: number) => {
    return rating.toFixed(1);
  };

  const formatReviewCount = (count: number) => {
    if (count === 0) return t('public.trainingCenterDetail.reviewCount.none');
    if (count === 1) return t('public.trainingCenterDetail.reviewCount.one');
    return t('public.trainingCenterDetail.reviewCount.many', { count });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !center) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('public.trainingCenterDetail.errors.notFound')}</h1>
          <p className="text-gray-600 mb-6">{error || t('view.notFound.subtitle')}</p>
          <button
            onClick={() => navigate('/centers')}
            className="bg-brand-primary text-white px-6 py-3 rounded-xl hover:bg-brand-primary/90 transition-colors"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-mint via-brand-accent to-brand-secondary">
        <div className="absolute inset-0 bg-black/5"></div>
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-primary/10 rounded-full animate-bounce" style={{animationDuration: '6s'}}></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-brand-accent/20 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 pt-32">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/centers')}
            className="group flex items-center gap-2 text-white/90 hover:text-white transition-colors mb-8 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl hover:bg-white/20 mt-4"
          >
            <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">{t('common.back')}</span>
          </motion.button>

          {/* Center Info */}
          <motion.div 
            className="text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            {/* Logo and Name */}
            <motion.div 
              className="flex items-center gap-6 mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <motion.div 
                className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 relative"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {center.logo ? (
                  <img 
                    src={center.logo.startsWith('http') ? center.logo : `${API_ENDPOINTS.UPLOADS}/${center.logo}`} 
                    alt={center.name}
                    className="w-12 h-12 object-contain rounded-lg bg-white p-1"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.parentElement?.querySelector('.logo-fallback');
                      if (fallback) {
                        (fallback as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div className="logo-fallback absolute inset-0 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl" style={{ display: center.logo ? 'none' : 'flex' }}>
                  <Building2 className="h-8 w-8 text-white" />
                </div>
              </motion.div>
              <div>
                <motion.h1 
                  className="text-2xl lg:text-3xl font-bold mb-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  {center.name}
                </motion.h1>
                <motion.div 
                  className="flex items-center gap-4 text-white/80 flex-wrap"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-current" />
                    <span className="font-semibold">{formatRating(center.ratingStats?.averageRating || 0)}</span>
                    <span className="text-sm">({formatReviewCount(center.ratingStats?.totalReviews || 0)})</span>
                  </div>
                  {center.category && (
                    <motion.div 
                      className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.9, type: "spring", stiffness: 300 }}
                    >
                      <Target className="h-3 w-3" />
                      <span className="text-xs font-medium">
                        {i18n.language === 'fr' ? center.category.name_french : center.category.name_english}
                      </span>
                    </motion.div>
                  )}
                  {center.isVerified && (
                    <motion.div 
                      className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1, type: "spring", stiffness: 300 }}
                    >
                      <CheckCircle className="h-3 w-3" />
                      <span className="text-xs font-medium">{t('public.trainingCenterDetail.verified')}</span>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </motion.div>

            {/* Stats and Action Buttons - All in one row */}
            <motion.div 
              className="flex flex-wrap items-center gap-3 sm:gap-4 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
            >
              {/* Stats */}
              {[
                { value: centerTrainings.length, label: t('public.trainingCenterDetail.stats.trainings'), color: "text-brand-primary" },
                { value: center.ratingStats?.totalReviews || 0, label: t('public.trainingCenterDetail.stats.reviews'), color: "text-brand-secondary" },
                { value: center.viewCount || 0, label: t('public.trainingCenterDetail.stats.views'), color: "text-white" },
                { value: "24/7", label: t('public.trainingCenterDetail.stats.support'), color: "text-brand-primary" }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 text-center flex-shrink-0"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.2 + index * 0.1, duration: 0.4 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                >
                  <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
                  <div className="text-xs text-white/80">{stat.label}</div>
                </motion.div>
              ))}

              {/* Action Buttons */}
              <motion.button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm rounded-xl font-semibold transition-all duration-200 flex-shrink-0"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.6, duration: 0.4 }}
              >
                <Share2 className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">{t('public.trainingCenterDetail.share.button')}</span>
              </motion.button>
              {isAuthenticated && isSubscribedToCenter && (
                <motion.button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-brand-primary text-white hover:bg-brand-primary/90 rounded-xl font-semibold transition-all duration-200 flex-shrink-0"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.7, duration: 0.4 }}
                >
                  <Star className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">{t('public.trainingCenterDetail.writeReview')}</span>
                  <span className="sm:hidden">{t('public.trainingCenterDetail.details.reviews')}</span>
                </motion.button>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Center Information Cards */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
            {/* Description Card */}
            <motion.div 
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -2, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
            >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-brand-primary" />
                    {t('public.trainingCenterDetail.details.description')}
                  </h2>
                  <button
                    onClick={() => toggleSection('description')}
                    className="text-brand-primary hover:text-brand-secondary transition-colors"
                  >
                    {expandedSections.description ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                </div>
                <div className={`transition-all duration-300 ${expandedSections.description ? 'max-h-none' : 'max-h-24 overflow-hidden'}`}>
                  <p className="text-gray-600 leading-relaxed">
                    {center.description || t('public.trainingCenterDetail.details.description')}
                  </p>
                </div>
              </motion.div>


              {/* Contact Information Card */}
              <motion.div 
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ y: -2, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-brand-accent" />
                    {t('public.trainingCenterDetail.details.contact')}
                  </h2>
                  <button
                    onClick={() => toggleSection('contact')}
                    className="text-brand-primary hover:text-brand-secondary transition-colors"
                  >
                    {expandedSections.contact ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                </div>
                <div className={`transition-all duration-300 ${expandedSections.contact ? 'max-h-none' : 'max-h-24 overflow-hidden'}`}>
                  <div className="space-y-4">
                    {/* Location */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-brand-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">
                          {(() => {
                            const countryName = center.country?.name || '';
                            const wilayaName = center.wilaya?.name || '';
                            
                            if (!countryName) return t('common.locationNotSpecified');
                            
                            // If country is Algeria, prioritize wilaya
                            if (countryName.toLowerCase() === 'algeria' || countryName.toLowerCase() === 'algérie') {
                              return wilayaName ? `${wilayaName}, ${countryName}` : countryName;
                            } else {
                              // For other countries, show country only
                              return countryName;
                            }
                          })()}
                        </p>
                        <p className="text-sm text-gray-600">{center.address || center.location || t('public.trainingCenterDetail.contact.addressNotAvailable')}</p>
                      </div>
                    </div>
                    {/* Phone */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-brand-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-brand-secondary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">{center.contact.phone || t('public.trainingCenterDetail.contact.notAvailable')}</p>
                        <p className="text-sm text-gray-600">{t('public.trainingCenterDetail.contact.phone')}</p>
                      </div>
                    </div>
                    {/* Email */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-brand-mint/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-5 w-5 text-brand-mint" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">{center.contact.email || t('public.trainingCenterDetail.contact.notAvailable')}</p>
                        <p className="text-sm text-gray-600">{t('public.trainingCenterDetail.contact.email')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.section>

            {/* Trainings Section */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-brand-primary" />
                  {t('public.trainingCenterDetail.trainings.title')} ({centerTrainings.length})
                </h2>
                    <Link
                  to={`/trainings?centerName=${encodeURIComponent(center?.name || '')}`}
                  className="text-brand-primary hover:text-brand-secondary transition-colors font-semibold flex items-center gap-1"
                >
                  {t('public.trainingCenterDetail.trainings.viewAll')}
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </div>

              {centerTrainings.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {centerTrainings.slice(0, 4).map((training, index) => (
                    <motion.div
                      key={training.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="group relative overflow-hidden bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all duration-300 hover:border-brand-primary/30"
                      whileHover={{ scale: 1.02, y: -2 }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                          <BookOpen className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-brand-primary transition-colors">
                            {training.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {training.short_description || t('common.professionalTraining')}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {training.duration || t('public.trainingCenterDetail.trainings.notAvailable')}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {training.level || t('public.trainingCenterDetail.trainings.allLevels')}
                            </div>
                          </div>
                        </div>
                      </div>
                      <Link
                        to={`/training/${training.id}`}
                        className="absolute inset-0"
                      />
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">{t('common.noResults')}</p>
                  </div>
                )}
            </motion.section>

            {/* Reviews Section */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Star className="h-5 w-5 text-brand-accent" />
                  {t('public.trainingCenterDetail.reviews.title')} ({reviews.length})
                </h2>
                {isAuthenticated && (
                  <button
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    className="bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-primary/90 transition-colors font-semibold"
                  >
                    {showReviewForm ? t('common.cancel') : t('public.trainingCenterDetail.writeReview')}
                  </button>
                )}
              </div>

              {/* Review Form */}
              {showReviewForm && isAuthenticated && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <h3 className="font-semibold text-gray-900 mb-4">{t('public.trainingCenterDetail.reviewForm.title')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('public.trainingCenterDetail.reviewForm.rating')}</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            onClick={() => setSelectedRating(rating)}
                            className={`p-1 transition-colors ${
                              selectedRating >= rating
                                ? 'text-brand-accent'
                                : 'text-gray-300 hover:text-gray-400'
                            }`}
                          >
                            <Star className="h-6 w-6 fill-current" />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('public.trainingCenterDetail.reviewForm.comment')}</label>
                      <textarea
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder={t('public.trainingCenterDetail.shareExperience')}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={handleReviewSubmit}
                        disabled={isSubmittingReview || selectedRating === 0}
                        className="bg-brand-primary text-white px-6 py-2 rounded-lg hover:bg-brand-primary/90 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmittingReview ? t('public.trainingCenterDetail.reviewForm.submitting') : t('public.trainingCenterDetail.reviewForm.submit')}
                      </button>
                      <button
                        onClick={() => setShowReviewForm(false)}
                        className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                      >
                        {t('public.trainingCenterDetail.reviewForm.cancel')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Review Filters */}
              <div className="flex flex-wrap gap-2 mb-6">
                {['all', '5', '4', '3', '2', '1'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setReviewFilter(filter as any)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      reviewFilter === filter
                        ? 'bg-brand-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter === 'all' ? t('public.trainingCenterDetail.reviewFilters.all') : t('public.trainingCenterDetail.reviewFilters.stars', { count: parseInt(filter) || 0 })}
                  </button>
                ))}
              </div>

              {/* Reviews List */}
              <div className="space-y-4">
                {filteredReviews.length > 0 ? (
                  filteredReviews.map((review, index) => (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">
                                {review.user.firstName && review.user.lastName 
                                  ? `${review.user.firstName} ${review.user.lastName}`
                                  : review.user.username
                                }
                              </h4>
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((rating) => (
                                  <Star
                                    key={rating}
                                    className={`h-4 w-4 ${
                                      rating <= review.rating
                                        ? 'text-brand-accent fill-current'
                                        : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(review.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <p className="text-gray-700 leading-relaxed">{review.comment}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">{t('public.trainingCenterDetail.noReviews')}</p>
                  </div>
                )}
              </div>
            </motion.section>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-brand-primary" />
                {t('public.trainingCenterDetail.details.quickInfo')}
              </h3>
              <div className="space-y-4">
                {center.category && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{t('public.trainingCenterDetail.details.category')}</span>
                    <span className="font-semibold text-brand-primary">
                      {i18n.language === 'fr' ? center.category.name_french : center.category.name_english}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('public.trainingCenterDetail.stats.averageRating')}</span>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-brand-accent fill-current" />
                    <span className="font-semibold">{formatRating(center.ratingStats?.averageRating || 0)}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('public.trainingCenterDetail.stats.reviewCount')}</span>
                  <span className="font-semibold">{center.ratingStats?.totalReviews || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('public.trainingCenterDetail.stats.trainings')}</span>
                  <span className="font-semibold">{centerTrainings.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('public.trainingCenterDetail.stats.status')}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    center.isVerified 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {center.isVerified ? t('common.verified') : t('common.notVerified')}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Social Media & Links Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-brand-primary to-brand-secondary rounded-2xl p-6 text-white"
            >
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {t('public.trainingCenterDetail.socialMedia.title')}
              </h3>
              <div className="space-y-3">
                {center.contact.website && (
                  <a 
                    href={center.contact.website} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <Globe className="h-4 w-4" />
                    </div>
                    <span className="text-sm">{t('public.trainingCenterDetail.contact.website')}</span>
                  </a>
                )}
                {(center.socialMedia?.facebook || (center as any).facebook_url) && (
                  <a 
                    href={(center.socialMedia?.facebook || (center as any).facebook_url) || ''} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <Facebook className="h-4 w-4" />
                    </div>
                    <span className="text-sm">Facebook</span>
                  </a>
                )}
                {(center.socialMedia?.linkedin || (center as any).linkedin_url) && (
                  <a 
                    href={(center.socialMedia?.linkedin || (center as any).linkedin_url) || ''} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <Linkedin className="h-4 w-4" />
                    </div>
                    <span className="text-sm">LinkedIn</span>
                  </a>
                )}
                {(!center.contact.website && !center.socialMedia?.facebook && !(center as any).facebook_url && !center.socialMedia?.linkedin && !(center as any).linkedin_url) && (
                  <p className="text-sm text-white/70">{t('public.trainingCenterDetail.socialMedia.notAvailable')}</p>
                )}
              </div>
            </motion.div>

            {/* Similar Centers Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-brand-secondary" />
                {t('public.trainingCenterDetail.similarCenters.title')}
              </h3>
              <div className="space-y-3">
                {similarCenters.length > 0 ? (
                  similarCenters.map((similarCenter, index) => (
                    <Link
                      key={similarCenter.id}
                      to={`/training-center/${similarCenter.id}`}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                        {similarCenter.logo ? (
                          <img 
                            src={similarCenter.logo.startsWith('http') ? similarCenter.logo : `${API_ENDPOINTS.UPLOADS}/${similarCenter.logo}`} 
                            alt={similarCenter.name}
                            className="w-full h-full object-contain rounded bg-white p-1 relative z-10"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.parentElement?.querySelector('.similar-logo-fallback');
                              if (fallback) {
                                (fallback as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div className="similar-logo-fallback absolute inset-0 flex items-center justify-center bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg" style={{ display: similarCenter.logo ? 'none' : 'flex' }}>
                          <Building2 className="h-4 w-4 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{similarCenter.name}</p>
                        <p className="text-xs text-gray-500">{similarCenter.wilaya?.name || t('common.notSpecified')}</p>
                        <p className="text-xs text-brand-primary">{similarCenter.totalTrainings} {t('public.trainingCenterDetail.similarCenters.trainings')}</p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <Building2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">{t('public.trainingCenterDetail.similarCenters.notFound')}</p>
                  </div>
                )}
              </div>
              <Link
                to="/centers"
                className="block w-full mt-4 text-center text-brand-primary hover:text-brand-secondary transition-colors font-semibold text-sm"
              >
                {t('public.trainingCenterDetail.similarCenters.viewAll')}
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingCenterDetailPage;